import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  Text,
  VStack,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';

import Card from 'components/card/Card';
import FileUploadCard from 'components/FileUploadCard';
import { DashReportForm } from 'views/dash_report/components';

import { COMMERCIAL_FILE_TYPES } from 'constants/fileTypes';
import {
  RUNNER_COMMERCIAL_AUTO,
  RUNNER_COMMERCIAL_PROPERTY_CASUALTY,
} from 'variables/constants';

import { apiGetCommercialFiles, apiDeleteCommercialFile } from 'api';

import { useDashReport, useCarriers } from 'hooks/index';
import { useAppSelector } from 'hooks';
import type { APIApplicationFile } from 'types/api.types';

interface CommercialDashReportProps {
  isLocked?: boolean;
}

const CommercialDashReport = ({
  isLocked = false,
}: CommercialDashReportProps) => {
  const { uuid } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Get the selected insurance company from Redux
  const insuranceCompany = useAppSelector(
    (state) => state.commercialForm.data.insurance_company,
  );
  const lineOfBusiness = useAppSelector(
    (state) => state.commercialForm.lineOfBusiness,
  );

  // Determine runner type based on line of business
  const runnerType =
    lineOfBusiness === 'Auto'
      ? RUNNER_COMMERCIAL_AUTO.toString()
      : RUNNER_COMMERCIAL_PROPERTY_CASUALTY.toString();

  // Get carriers data from carriers hook
  const { carriers, isLoadingCarriers } = useCarriers({
    runner_type: runnerType,
  });

  // Use the DASH report hook with policy UUID
  const { generateReport, isSubmitting } = useDashReport({
    policyUuid: uuid,
    onSuccess: () => {
      // Refresh the files list when DASH report is generated successfully
      queryClient.invalidateQueries({ queryKey: ['commercial-files', uuid] });
    },
  });

  // Query for existing DASH report files (must be called before any returns)
  const { data: files = [] } = useQuery({
    queryKey: ['commercial-files', uuid],
    queryFn: () => apiGetCommercialFiles(uuid!),
    refetchOnWindowFocus: false,
  }) as { data: APIApplicationFile[] };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (fileId: number) => apiDeleteCommercialFile(uuid!, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commercial-files', uuid] });
      toast({
        title: 'File deleted',
        description: 'DASH report removed successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: () => {
      toast({
        title: 'Delete failed',
        description: 'Failed to delete file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  // Check if selected carrier supports DASH reports (Aviva Elite)
  const shouldShowDASH = insuranceCompany === 'Aviva Elite';

  if (!shouldShowDASH) {
    return null;
  }

  // Find the selected carrier from the carriers list
  const selectedCarrier =
    carriers.find((c) => c.name === insuranceCompany) || null;

  // Check if the carrier has DASH enabled in the database
  const isDashEnabled = selectedCarrier?.is_dash || false;

  // Filter for DASH report files
  const dashFiles = files.filter(
    (file) => file.file_type === COMMERCIAL_FILE_TYPES.DASH_REPORT,
  );

  // Create carrier options for the form
  const carrierOptions = carriers.map((carrier) => ({
    value: carrier.id,
    label: carrier.name,
  }));

  const handleRemoveFile = (fileId: number) => {
    deleteMutation.mutate(fileId);
  };

  return (
    <Card mb="20px" id="sectionCommercialDashReport">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        DASH Report Generation
      </Text>

      <VStack spacing={4} align="stretch">
        {!isDashEnabled ? (
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription>
              DASH report generation is not enabled for{' '}
              {selectedCarrier?.name || insuranceCompany}. Please contact your
              administrator if you need this feature enabled.
            </AlertDescription>
          </Alert>
        ) : (
          <DashReportForm
            carrierOptions={carrierOptions}
            carriers={carriers}
            isLoadingCarriers={isLoadingCarriers}
            onSubmit={generateReport}
            submitButtonText="Generate DASH Report"
            defaultCarrier={selectedCarrier}
            disableCarrierSelection={true}
            isSubmitting={isSubmitting}
          />
        )}

        {dashFiles.length > 0 && (
          <VStack spacing={3} align="stretch">
            <Text fontSize="sm" fontWeight="500" color="gray.600">
              Generated DASH Reports:
            </Text>
            {dashFiles.map((file) => (
              <FileUploadCard
                key={file.id}
                file={file}
                badgeText="DASH Report"
                badgeColorScheme="blue"
                onDelete={handleRemoveFile}
                isDeleting={deleteMutation.isPending}
                isLocked={isLocked}
              />
            ))}
          </VStack>
        )}
      </VStack>
    </Card>
  );
};

export default CommercialDashReport;
